import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Divider, Typography, IconButton, InputBase, Paper, CircularProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack, Collapse } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useSnackbar } from '../providers/SnackBarProvider';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

function AssistantPanel() {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const location = useLocation();
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // Función helper para obtener la sección actual de la app
  const getAppSection = () => {
    const pathname = location.pathname;
    // Eliminar el prefijo '/main/' y obtener la primera parte de la ruta
    const section = pathname.replace(/^\/main\//, '').split('/')[0];
    return section || null;
  };

  // Función helper para obtener el ID de la sección (si aplica)
  const getAppSectionId = () => {
    const section = getAppSection();
    // Solo para secciones de "view" que tengan un ID en el state
    if (section && section.startsWith('view-') && location.state?.id) {
      return location.state.id;
    }
    return null;
  };

  const handleSend = async () => {
    const trimmed = inputText.trim();
    if (!trimmed && uploadedFiles.length === 0) {
      showSnackbar(t('assistant.writeOrAttach'), 'warning');
      return;
    }
    
    try {
      setIsSending(true);
      
      // Agregar mensaje del usuario
      const userMessage = { 
        role: 'user', 
        content: trimmed,
        files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined
      };
      setMessages(prev => [...prev, userMessage]);
      setInputText('');
      
      // Limpiar archivos adjuntos después de agregarlos al mensaje
      const filesForRequest = [...uploadedFiles];
      setUploadedFiles([]);
      
      // Preparar mensaje del asistente
      const assistantMessage = { role: 'assistant', content: '' };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Forzar scroll al final después de agregar los mensajes
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      // Obtener la sección actual de la app y su ID (si aplica)
      const appSection = getAppSection();
      const appSectionId = getAppSectionId();
      
      // Extraer solo las URLs de los archivos para enviar al backend
      const filesUrls = filesForRequest.length > 0 
        ? filesForRequest.map(file => file.url)
        : [];
      
      // Hacer request con streaming
      const response = await fetch(`${import.meta.env.VITE_API_URL}ai?chat_completions_app`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          message: trimmed,
          id: conversationId || '',
          ...(appSection && { app_section: appSection }),
          ...(appSectionId && { app_section_id: appSectionId }),
          ...(filesUrls.length > 0 && { files_urls: filesUrls })
        }),
      });
      
      //console.log(response);
      if (!response.ok) {
        // Intentar parsear el error del backend
        let errorMessage = t('assistant.serverError');
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Si no se puede parsear, usar el mensaje genérico ya definido
          console.error('Error parseando respuesta de error:', parseError);
        }
        throw new Error(errorMessage);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedConversationId = null;
      let receivedTitle = null;
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (!line.trim()) continue;
          
          try {
            const data = JSON.parse(line);
            
            // Guardar el ID de conversación
            if (data.id && !receivedConversationId) {
              receivedConversationId = data.id;
              if (!conversationId) {
                setConversationId(data.id);
                
                // Actualizar el chat sin ID con el id real
                setChats(prev => {
                  const newChat = prev.find(c => !c.id);
                  if (newChat) {
                    return prev.map(c => 
                      !c.id 
                        ? { ...c, id: data.id }
                        : c
                    );
                  }
                  return prev;
                });
                
                setActiveChat(data.id);
              }
            }
            
            // Procesar el thinking
            if (data.choices && data.choices[0]?.delta?.thinking) {
              const thinking = data.choices[0].delta.thinking;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.thinking = (lastMessage.thinking || '') + thinking;
                }
                return newMessages;
              });
            }
            
            // Procesar el contenido
            if (data.choices && data.choices[0]?.delta?.content) {
              const content = data.choices[0].delta.content;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content += content;
                  // Limpiar el thinking cuando empieza a llegar contenido
                  if (!lastMessage.thinkingDone && lastMessage.thinking) {
                    lastMessage.thinkingDone = true;
                  }
                }
                return newMessages;
              });
            }
            
            // Capturar el título cuando llega en el último chunk
            if (data.choices && data.choices[0]?.delta?.title) {
              receivedTitle = data.choices[0].delta.title;
            }
          } catch (err) {
            // Ignorar líneas que no son JSON válido
          }
        }
      }
      
      // Actualizar el título del chat si se recibió
      if (receivedTitle && receivedConversationId) {
        setChats(prev => {
          return prev.map(c => 
            c.id === receivedConversationId 
              ? { ...c, title: receivedTitle }
              : c
          );
        });
      }
      
    } catch (err) {
      // Mostrar el error del backend
      showSnackbar(err.message, 'error');
      //console.error(err);
      
      // Eliminar los mensajes que se agregaron (usuario y asistente vacío)
      setMessages(prev => prev.slice(0, -2));
    } finally {
      setIsSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('file', audioBlob, 'audio.webm');
          setIsSending(true);
          const res = await axios.post(`${import.meta.env.VITE_API_URL}ai?whisper`, formData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          
          //console.log(res);

          const text = res.data.data;
          if (text) {
            setInputText((prev) => (prev ? `${prev} ${text}` : text));
            //showSnackbar('Transcripción agregada', 'success');
          } else {
            showSnackbar(t('assistant.noTranscription'), 'warning');
          }
        } catch (err) {
          showSnackbar(err.response.data.message, 'error');
        } finally {
          setIsSending(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      showSnackbar(t('assistant.recordingHint'), 'info');
    } catch (err) {
      showSnackbar(t('assistant.micError'), 'error');
    }
  };

  const stopRecording = () => {
    try {
      const mediaRecorder = mediaRecorderRef.current;
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
      // Detener tracks del stream para liberar el micrófono
      const tracks = mediaRecorder?.stream?.getTracks?.() || [];
      tracks.forEach((t) => t.stop());
    } catch (err) {
      // noop
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;
    
    // Validar máximo 10 archivos
    if (files.length > 10) {
      showSnackbar(t('assistant.maxFiles'), 'warning');
      return;
    }
    
    // Validar que no exceda el límite junto con los archivos ya subidos
    if (uploadedFiles.length + files.length > 10) {
      showSnackbar(t('assistant.filesLimitRemaining', { count: 10 - uploadedFiles.length }), 'warning');
      return;
    }
    
    try {
      setIsUploading(true);
      const formData = new FormData();
      
      // Agregar todos los archivos al FormData
      files.forEach((file) => {
        formData.append('files[]', file);
      });
      
      // Si hay un conversationId, agregarlo al contexto
      if (conversationId) {
        formData.append('conversation_id', conversationId);
      }
      
      // Subir archivos al backend
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}s3_upload_chat`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            if (percentCompleted < 100) {
              showSnackbar(t('assistant.uploadingPercent', { percent: percentCompleted }), 'info');
            }
          }
        }
      );
      
      // Verificar respuesta
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const uploadedFilesData = response.data.data;
        
        // Mapear los archivos con sus nombres originales y URLs
        const filesWithInfo = uploadedFilesData.map((fileData, index) => ({
          url: fileData.url,
          name: files[index]?.name || t('assistant.unnamedFile'),
          originalFile: files[index]
        }));
        
        // Agregar los archivos subidos al estado
        setUploadedFiles(prev => [...prev, ...filesWithInfo]);
        
        showSnackbar(t('assistant.filesUploaded', { count: files.length }), 'success');
      } else {
        throw new Error(t('assistant.serverError'));
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('assistant.uploadError');
      showSnackbar(errorMessage, 'error');
      console.error('Error subiendo archivos:', error);
    } finally {
      setIsUploading(false);
      // Limpiar el input para permitir subir los mismos archivos nuevamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = (fileIndex) => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== fileIndex));
    showSnackbar(t('assistant.fileDeleted'), 'info');
  };

  // Cargar charlas desde el servidor
  const loadChats = async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}ai?chats`, {
        withCredentials: true,
      });
      const loadedChats = response.data.data || [];
      
      if (loadedChats.length > 0) {
        // Si hay chats anteriores, mostrar el primero
        setChats(loadedChats);
        const firstChat = loadedChats[0];
        setActiveChat(firstChat.id);
        setConversationId(firstChat.id);
        // Cargar mensajes del primer chat
        await loadChatMessages(firstChat.id);
      } else {
        // Si no hay chats anteriores, crear una charla nueva vacía
        const newChat = {
          id: null,
          title: t('assistant.newChat')
        };
        setChats([newChat]);
        setActiveChat(null);
        setMessages([]);
        setConversationId(null);
      }
    } catch (err) {
      console.error('Error cargando chats:', err);
      const newChat = {
        id: null,
        title: t('assistant.newChat')
      };
      setChats([newChat]);
      setActiveChat(null);
      setMessages([]);
      setConversationId(null);
    }
  };

  // Cargar mensajes de un chat específico
  const loadChatMessages = async (chatId) => {
    // Solo cargar si el chat tiene ID
    if (!chatId) {
      return;
    }

    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}ai?chat_id=${chatId}`, {
        withCredentials: true,
      });
      const chatData = response.data.data;
      if (chatData && chatData.messages) {
        setMessages(chatData.messages);
      }
    } catch (err) {
      console.error('Error cargando mensajes del chat:', err);
      showSnackbar(t('assistant.loadMessagesError'), 'error');
    }
  };

  // Crear nueva charla
  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    
    // Crear un nuevo chat sin ID
    const newChat = {
      id: null,
      title: t('assistant.newChat')
    };
    
    // Agregar al inicio de la lista de chats
    setChats(prev => [newChat, ...prev]);
    setActiveChat(null);
  };

  // Cerrar charla
  const handleCloseChat = async (e, chatId) => {
    e.stopPropagation();
    try {
      // Solo hacer petición al backend si el chat tiene ID
      if (chatId) {
        // Marcar la charla como cerrada en el backend
        await axios.put(
          `${import.meta.env.VITE_API_URL}ai?update_chat_opened&id=${chatId}&opened=0`, 
          null,
          { withCredentials: true }
        );
      }
      
      const updatedChats = chats.filter(chat => chat.id !== chatId);
      setChats(updatedChats);
      
      // Si la charla cerrada era la activa, cambiar a otra
      if (activeChat === chatId) {
        if (updatedChats.length > 0) {
          const nextChat = updatedChats[0];
          setActiveChat(nextChat.id);
          setConversationId(nextChat.id);
          // Cargar mensajes del siguiente chat
          await loadChatMessages(nextChat.id);
        } else {
          handleNewChat();
        }
      }
    } catch (err) {
      showSnackbar(t('assistant.closeChatError'), 'error');
      console.error(err);
    }
  };

  // Cambiar de charla activa
  const handleChangeChat = async (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (chat) {
      setActiveChat(chatId);
      setConversationId(chatId);
      // Cargar mensajes del chat seleccionado
      await loadChatMessages(chatId);
    }
  };

  // Cargar charlas al montar el componente
  useEffect(() => {
    loadChats();
  }, []);

  // Ya no necesitamos recargar las charlas automáticamente porque
  // creamos el chat localmente y lo actualizamos con los datos del backend
  // Solo recargar si realmente es necesario
  useEffect(() => {
    if (conversationId && chats.length > 0) {
      const existingChat = chats.find(c => c.id === conversationId);
      // Si no existe o no tiene ID, no hacer nada (ya lo manejamos en handleSend)
      if (!existingChat || !existingChat.id) {
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Scroll automático solo si el usuario está cerca del final
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Verificar si el usuario está cerca del final (dentro de 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const markdownComponents = useMemo(() => ({
    h1: ({ children }) => (
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 0.5 }}>{children}</Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>{children}</Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>{children}</Typography>
    ),
    p: ({ children }) => (
      <Typography variant="body2" sx={{ mb: 0.5 }}>{children}</Typography>
    ),
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ ml: 2 }}>{children}</Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 2, my: 0.5 }}>{children}</Box>
    ),
    hr: () => <Divider sx={{ my: 2 }} />,
    table: ({ children }) => (
      <TableContainer component={Paper} variant="outlined" sx={{ my: 2, maxWidth: '100%', overflow: 'auto' }}>
        <Table size="small">{children}</Table>
      </TableContainer>
    ),
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => (
      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover', fontSize: '0.70rem', py: 0.5, px: 1 }}>
        {children}
      </TableCell>
    ),
    td: ({ children }) => (
      <TableCell sx={{ fontSize: '0.70rem', py: 0.5, px: 1 }}>{children}</TableCell>
    ),
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{children}</a>
    ),
    code: ({ children, className }) => {
      const isBlock = className?.startsWith('language-');
      if (isBlock) {
        return (
          <Box component="pre" sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, my: 1, overflow: 'auto', fontSize: '0.8rem' }}>
            <code>{children}</code>
          </Box>
        );
      }
      return (
        <Box component="code" sx={{ bgcolor: 'action.hover', borderRadius: 0.5, px: 0.5, fontSize: '0.85em' }}>
          {children}
        </Box>
      );
    },
    pre: ({ children }) => <>{children}</>,
  }), []);

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: (theme) => theme.palette.background.paper,
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
      {/* Tabs de charlas - fijos arriba */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.background.paper,
      }}>
        <Tabs 
          value={activeChat || false}
          onChange={(e, newValue) => handleChangeChat(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1, minHeight: 48 }}
        >
          {chats.map((chat, index) => (
            <Tab 
              key={chat.id || `new-chat-${index}`}
              value={chat.id || false}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                    {chat.title || t('assistant.newChat')}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleCloseChat(e, chat.id)}
                    sx={{ 
                      p: 0.25, 
                      ml: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              }
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          ))}
        </Tabs>
        <IconButton
          onClick={handleNewChat}
          size="small"
          sx={{ mx: 1 }}
          title={t('assistant.newChat')}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* Header - solo visible cuando no hay charla activa */}
      {/* {!activeChat && (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Asistente
          </Typography>
          <Typography variant="h6">
            IA Doctor Vet
          </Typography>
        </Box>
      )}
      <Divider /> */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          p: 2, 
          flex: 1, 
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('assistant.helpText')}
          </Typography>
        ) : (
          messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: msg.role === 'user' 
                    ? 'primary.main' 
                    : 'background.default',
                  color: msg.role === 'user' 
                    ? 'primary.contrastText' 
                    : 'text.primary',
                }}
              >
                {/* Mostrar archivos adjuntos si existen */}
                {msg.files && msg.files.length > 0 && (
                  <Box sx={{ mb: msg.content ? 1 : 0 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {msg.files.map((file, fileIndex) => (
                        <Chip
                          key={fileIndex}
                          icon={<InsertDriveFileIcon />}
                          label={file.name || file.file_name || t('assistant.fileN', { n: fileIndex + 1 })}
                          size="small"
                          sx={{ 
                            mb: 0.5,
                            bgcolor: msg.role === 'user' 
                              ? 'rgba(255, 255, 255, 0.2)' 
                              : 'action.hover',
                            color: msg.role === 'user' 
                              ? 'primary.contrastText' 
                              : 'text.primary',
                            '& .MuiChip-icon': {
                              color: msg.role === 'user' 
                                ? 'primary.contrastText' 
                                : 'text.secondary'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                {/* Thinking del asistente */}
                {msg.role === 'assistant' && msg.thinking && !msg.thinkingDone && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: msg.content ? 1 : 0,
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      fontSize: '0.85rem'
                    }}
                  >
                    <PsychologyIcon sx={{ fontSize: 16, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                      {msg.thinking}
                    </Typography>
                  </Box>
                )}
                
                {/* Contenido del mensaje */}
                <Box sx={{ wordBreak: 'break-word' }}>
                  {msg.role === 'assistant' && msg.content === '' && !msg.thinking ? (
                    <CircularProgress size={18} />
                  ) : msg.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  ) : null}
                </Box>
              </Paper>
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>
      <Divider />
      
      {/* Área de archivos adjuntos */}
      {uploadedFiles.length > 0 && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {uploadedFiles.map((file, index) => (
              <Chip
                key={index}
                icon={<InsertDriveFileIcon />}
                label={file.name || file.file_name || t('assistant.fileN', { n: index + 1 })}
                onDelete={() => handleRemoveFile(index)}
                size="small"
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box>
      )}
      
      <Box sx={{ p: 1 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            px: 1,
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            multiple
          />
          <IconButton
            color="primary"
            onClick={handleAttachClick}
            aria-label={t('assistant.attachFiles')}
            disabled={isSending || isUploading || uploadedFiles.length >= 10}
            size="small"
            sx={{ mr: 1 }}
          >
            <AttachFileIcon />
          </IconButton>
          <InputBase
            placeholder={t('assistant.placeholder')}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={4}
            /*disabled={isSending}*/
            sx={{ flex: 1, py: 1 }}
          />
          <IconButton
            color={isRecording ? 'error' : 'primary'}
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={isRecording ? t('assistant.stopRecording') : t('assistant.startRecording')}
            disabled={isSending || isUploading}
            size="small"
            sx={{ mr: 1 }}
          >
            {isRecording ? <StopIcon /> : <MicIcon />}
          </IconButton>
          <IconButton
            color="primary"
            onClick={handleSend}
            aria-label={t('assistant.send')}
            disabled={isSending || isUploading}
            size="small"
          >
            {isSending || isUploading ? <CircularProgress size={18} /> : <SendIcon />}
          </IconButton>
        </Paper>
      </Box>
    </Box>
    </>
  );
}

export default AssistantPanel;


